import {
  ReturnDropEventType,
  VirtualMiddleMileCarrier,
  virtualMiddleMileCarrierOf,
} from "@redotech/redo-return-dropoff-provider-common/virtual-middle-mile";
import {
  ReturnDropRetirementReason,
  type ReturnDropTrackingEvent,
  type VirtualQrCodeMetadata,
} from "./dropoff-types";

/** Oldest first; ties keep a stable order by event id. */
export function returnDropEventsInOrder(
  metadata: VirtualQrCodeMetadata,
): ReturnDropTrackingEvent[] {
  return Object.values(metadata.returnDropEvents ?? {}).sort(
    (a, b) => a.occurredAt - b.occurredAt || a.eventId.localeCompare(b.eventId),
  );
}

/** A parcel cannot reach the returns centre without being dropped off first. */
const DROPPED_OFF_EVENT_TYPES = new Set<string>([
  ReturnDropEventType.CUSTOMER_DROPOFF,
  ReturnDropEventType.DELIVERED,
]);

/** The tracking poller records the events; the hub's verification webhook records `hubVerifiedAt`. */
export function isReturnDropDroppedOff(
  metadata: VirtualQrCodeMetadata,
): boolean {
  return (
    metadata.hubVerifiedAt != null ||
    Object.values(metadata.returnDropEvents ?? {}).some((event) =>
      DROPPED_OFF_EVENT_TYPES.has(event.type),
    )
  );
}

/**
 * Retirements are only ever added, so the strongest one wins. "Unused" lapses
 * once the parcel is known to be dropped off, which resumes polling.
 */
export function returnDropRetirementOf(
  metadata: VirtualQrCodeMetadata,
): ReturnDropRetirementReason | undefined {
  const retiredAt = metadata.returnDropRetiredAt ?? {};
  if (retiredAt.delivered !== undefined) {
    return ReturnDropRetirementReason.DELIVERED;
  }
  if (retiredAt.not_found !== undefined) {
    return ReturnDropRetirementReason.NOT_FOUND;
  }
  if (retiredAt.stalled !== undefined) {
    return ReturnDropRetirementReason.STALLED;
  }
  if (retiredAt.unused !== undefined && !isReturnDropDroppedOff(metadata)) {
    return ReturnDropRetirementReason.UNUSED;
  }
  return undefined;
}

/**
 * The stored form of "the poll still tracks this entry", so its index spans only
 * those. The dash code is the poll filter's to check, being written later.
 */
export function isReturnDropTrackingOpen(
  metadata: VirtualQrCodeMetadata,
): boolean {
  return (
    virtualMiddleMileCarrierOf(metadata) ===
      VirtualMiddleMileCarrier.RETURN_DROP &&
    metadata.returnDropTrackingId !== undefined &&
    returnDropRetirementOf(metadata) === undefined
  );
}
